import type { Payload, Where } from 'payload'

import { citySelectOptions, type LocationCityId } from '@/collections/Locations/cityOptions'
import type { Locale } from '@/lib/i18n/config'
import {
  getCanonicalLocationSlug,
  getPayloadLocationSlugCandidates,
} from '@/lib/locations/legacyLocationSlugs'
import { resolveLocationCitySlug } from '@/lib/locations/resolveLocationCitySlug'
import { getRoomTypeLabel, type RoomTypeId } from '@/lib/apartments/roomTypeOptions'
import {
  resolveApartmentListingFilters,
  type ApartmentListingFilters,
} from '@/lib/apartments/apartmentListingFilterOptions'
import { resolveApartmentFeatures } from '@/lib/apartments/apartmentFeatureOptions'
import { normalizeHttpsUrl } from '@/lib/urls/normalizeHttpsUrl'
import {
  getApartmentListingImages,
  mapApartmentGalleryViews,
  type ApartmentGalleryViews,
  type ApartmentImage,
} from '@/lib/apartments/apartmentGallery'
import {
  hasCommunitySpaceContent,
  mapLocationCommunitySpaces,
  type LocationCommunitySpaces,
} from '@/lib/locations/locationCommunitySpaces'

export const APARTMENTS_LIST_LIMIT = 200

export const APARTMENT_LIST_SELECT = {
  id: true,
  title: true,
  slug: true,
  roomType: true,
  isPrimaryVariant: true,
  location: true,
  shortDescription: true,
  description: true,
  bookingUrl: true,
  externalBookingUrl: true,
  size: true,
  peopleCapacity: true,
  roomCount: true,
  price: true,
  rentPrimaryText: true,
  rentSecondaryText: true,
  images: true,
  view360: true,
  furnishedView: true,
  amenities: {
    amenity: true,
    category: true,
  },
  features: true,
  listingFilters: {
    furnishing: true,
    hasBalcony: true,
    hasPatio: true,
    isAccessible: true,
  },
  popularChoice: {
    enabled: true,
    viewsFrom: true,
    viewsTo: true,
  },
} as const

export const APARTMENT_DETAIL_SELECT = {
  ...APARTMENT_LIST_SELECT,
  floorPlans: true,
  meta: true,
} as const

import type { AmenityIcon } from '@/lib/amenities/amenityIconOptions'
import type { AmenityDescription } from '@/lib/amenities/amenityDescription'
import type { Amenity, Apartment as CmsApartment, Location, Media } from '@/payload-types'
import { getMediaUrl } from '@/utilities/getMediaUrl'

export type { ApartmentGalleryViews, ApartmentImage } from '@/lib/apartments/apartmentGallery'
export type { LocationCommunitySpaces } from '@/lib/locations/locationCommunitySpaces'

export type ApartmentAmenityDetail = {
  category: 'included' | 'onDemand'
  description?: AmenityDescription | null
  icon?: AmenityIcon
  id: string
  image?: ApartmentImage
  title: string
}

export type ApartmentPopularChoice = {
  enabled: boolean
  viewsFrom: number
  viewsTo: number
}

export type ApartmentAvailabilityItem = {
  amenityDetails?: ApartmentAmenityDetail[]
  amenities: string[]
  /** Salesforce widget URL for the booking-page iframe, when set. */
  bookingUrl: string | null
  /** Direct Check availability destination. When set, the button opens this page instead of the booking page. */
  externalBookingUrl: string | null
  /** Full rich-text description, shown on the room detail page. */
  content: CmsApartment['description'] | null
  /** Short teaser (1–2 sentences), shown on listing cards and the chooser summary. */
  description: string
  /** Per-location community spaces content from CMS, when configured. */
  communitySpaces?: LocationCommunitySpaces | null
  features: ApartmentFeatureId[]
  galleryViews: ApartmentGalleryViews
  id: string
  images: ApartmentImage[]
  isPrimaryVariant: boolean
  listingFilters: Required<ApartmentListingFilters>
  location: string
  locationCitySlug: LocationCityId
  locationCityTitle: string
  locationId: number
  locationSlug: string
  locationTitle: string
  name: string
  peopleCapacity: number
  popularChoice?: ApartmentPopularChoice | null
  roomCount: number
  price: number
  rentPrimaryText: string
  rentSecondaryText: string
  roomType: RoomTypeId
  roomTypeLabel: string
  size: number
  slug: string
}

export type RoomTypeGroupSource = Pick<
  ApartmentAvailabilityItem,
  | 'description'
  | 'id'
  | 'images'
  | 'isPrimaryVariant'
  | 'location'
  | 'locationId'
  | 'name'
  | 'peopleCapacity'
  | 'price'
  | 'roomType'
  | 'roomTypeLabel'
  | 'size'
> & {
  amenities?: ApartmentAvailabilityItem['amenities']
}

export type LocationGroupSource = Pick<
  ApartmentAvailabilityItem,
  'id' | 'isPrimaryVariant' | 'location' | 'locationId' | 'locationSlug' | 'price'
> & {
  communitySpaces?: ApartmentAvailabilityItem['communitySpaces']
}

export type RoomTypeListingItem<T extends RoomTypeGroupSource = ApartmentAvailabilityItem> = T & {
  /** Upper bound for size display ranges (e.g. "17 m² to 18 m²"). */
  sizeMax?: number
  /** Lowest price across sibling variants in this room-type group. */
  startingPrice: number
  /** All sibling variants at the same location for this room type. */
  variants: T[]
}

export const LOCATION_CAROUSEL_IMAGE_LIMIT = 5

/** Landing location chooser default: pin this campus first, then sort the rest A–Z. */
export const PREFERRED_LANDING_LOCATION_SLUG = 'berlin-mitte-wedding'

export type LocationDropdownOption = {
  apartmentId: string
  location: string
  locationId: number
  price: number
}

const CITY_LABELS = Object.fromEntries(
  citySelectOptions.map((option) => [option.value, option.label]),
) as Record<LocationCityId, string>

type FindApartmentsOptions = {
  locale: Locale
  locationId?: number | null
  limit?: number
}

type FindApartmentByPathOptions = {
  apartmentSlug: string
  city: LocationCityId
  locale: Locale
  locationSlug: string
}

type FindApartmentBookingUrlBySlugOptions = {
  locale: Locale
  slug: string
}

export function buildApartmentsWhere(locationId?: number | null): Where | undefined {
  if (locationId == null) return undefined

  return {
    location: {
      equals: locationId,
    },
  }
}

function resolveMedia(media: number | Media | null | undefined): Media | null {
  if (!media || typeof media === 'number') return null
  return media
}

function resolveAmenity(amenity: number | Amenity | null | undefined): Amenity | null {
  if (!amenity || typeof amenity === 'number') return null
  return amenity
}

function formatLocationLabel(location: number | Location | null | undefined): string {
  if (!location || typeof location === 'number') return ''

  const citySlug = resolveLocationCitySlug(location)
  const cityTitle =
    typeof location.city === 'object' && location.city?.title?.trim()
      ? location.city.title.trim()
      : citySlug
        ? (CITY_LABELS[citySlug] ?? citySlug)
        : ''
  const title = location.title?.trim()

  if (!title) return cityTitle
  return `${cityTitle}, ${title}`
}

function resolveAmenityTitles(amenities: CmsApartment['amenities'] | null | undefined): string[] {
  const titles: string[] = []
  const seen = new Set<string>()

  for (const row of amenities ?? []) {
    const amenity = resolveAmenity(row.amenity)
    const title = amenity?.title?.trim()
    if (!title || seen.has(title)) continue

    seen.add(title)
    titles.push(title)
  }

  return titles
}

function resolveAmenityDetails(
  amenities: CmsApartment['amenities'] | null | undefined,
): ApartmentAmenityDetail[] {
  const details: ApartmentAmenityDetail[] = []
  const seen = new Set<number>()

  for (const row of amenities ?? []) {
    const amenity = resolveAmenity(row.amenity)
    const title = amenity?.title?.trim()

    if (!amenity || !title || seen.has(amenity.id)) continue

    seen.add(amenity.id)

    const media = resolveMedia(amenity.image)
    const src = getMediaUrl(media?.url)

    details.push({
      category: row.category,
      id: String(amenity.id),
      title,
      ...(amenity.description ? { description: amenity.description } : {}),
      ...(amenity.icon ? { icon: amenity.icon as AmenityIcon } : {}),
      ...(src
        ? {
            image: {
              alt: media?.alt?.trim() ?? '',
              src,
              ...(media?.height != null ? { height: media.height } : {}),
              ...(media?.width != null ? { width: media.width } : {}),
            },
          }
        : {}),
    })
  }

  return details
}

function normalizeRoomCount(value: number | null | undefined): number {
  const roomCount = Number(value)
  return Number.isFinite(roomCount) && roomCount > 0 ? roomCount : 1
}

export function mapApartmentToAvailabilityItem(
  apartment: Pick<
    CmsApartment,
    | 'id'
    | 'title'
    | 'slug'
    | 'roomType'
    | 'isPrimaryVariant'
    | 'location'
    | 'shortDescription'
    | 'description'
    | 'bookingUrl'
    | 'externalBookingUrl'
    | 'size'
    | 'peopleCapacity'
    | 'roomCount'
    | 'price'
    | 'rentPrimaryText'
    | 'rentSecondaryText'
    | 'images'
    | 'floorPlans'
    | 'view360'
    | 'furnishedView'
    | 'amenities'
    | 'features'
    | 'listingFilters'
    | 'popularChoice'
  >,
): ApartmentAvailabilityItem | null {
  const slug = apartment.slug?.trim() ?? ''
  const fallbackAlt = apartment.title

  const galleryViews = mapApartmentGalleryViews({
    images: apartment.images,
    floorPlans: apartment.floorPlans,
    view360: apartment.view360,
    furnishedView: apartment.furnishedView,
    fallbackAlt,
  })
  const images = getApartmentListingImages(galleryViews.gallery)

  if (images.length === 0) return null

  const locationLabel = formatLocationLabel(apartment.location)
  if (!locationLabel || typeof apartment.location !== 'object') return null

  const rawLocationSlug = apartment.location.slug?.trim()
  const locationCitySlug = resolveLocationCitySlug(apartment.location)
  if (!rawLocationSlug || !locationCitySlug) return null

  const locationSlug = getCanonicalLocationSlug(rawLocationSlug)

  const locationTitle = apartment.location.title?.trim() || locationLabel
  const locationCityTitle =
    typeof apartment.location.city === 'object' && apartment.location.city?.title?.trim()
      ? apartment.location.city.title.trim()
      : locationCitySlug
        ? (CITY_LABELS[locationCitySlug] ?? locationCitySlug)
        : ''
  const communitySpaces = mapLocationCommunitySpaces(
    apartment.location.communitySpaces,
    `${locationTitle} community spaces`,
  )

  return {
    id: String(apartment.id),
    slug,
    name: apartment.title,
    roomType: apartment.roomType,
    roomTypeLabel: getRoomTypeLabel(apartment.roomType),
    isPrimaryVariant: Boolean(apartment.isPrimaryVariant),
    location: locationLabel,
    locationCitySlug,
    locationCityTitle,
    locationId: apartment.location.id,
    locationSlug,
    locationTitle,
    description: apartment.shortDescription?.trim() ?? '',
    content: apartment.description ?? null,
    bookingUrl: normalizeHttpsUrl(apartment.bookingUrl),
    externalBookingUrl: normalizeHttpsUrl(apartment.externalBookingUrl),
    size: Number(apartment.size),
    peopleCapacity: Number(apartment.peopleCapacity),
    roomCount: normalizeRoomCount(apartment.roomCount),
    price: Number(apartment.price),
    rentPrimaryText: apartment.rentPrimaryText?.trim() ?? '',
    rentSecondaryText: apartment.rentSecondaryText?.trim() ?? '',
    galleryViews,
    images,
    amenities: resolveAmenityTitles(apartment.amenities),
    amenityDetails: resolveAmenityDetails(apartment.amenities),
    features: resolveApartmentFeatures(apartment.features),
    listingFilters: resolveApartmentListingFilters(apartment.listingFilters),
    popularChoice: mapPopularChoice(apartment.popularChoice),
    communitySpaces,
  }
}

export function groupApartmentsByLocation<T extends LocationGroupSource>(
  apartments: T[],
  options?: { requireCommunitySpaces?: boolean },
): LocationDropdownOption[] {
  const primaryApartments = apartments.filter((apartment) => apartment.isPrimaryVariant)
  const source = primaryApartments.length > 0 ? primaryApartments : apartments
  const byLocation = new Map<number, LocationDropdownOption>()
  const locationSlugs = new Map<number, string>()

  for (const apartment of source) {
    if (options?.requireCommunitySpaces && !hasCommunitySpaceContent(apartment.communitySpaces)) {
      continue
    }

    locationSlugs.set(apartment.locationId, apartment.locationSlug)

    const existing = byLocation.get(apartment.locationId)

    if (!existing || apartment.price < existing.price) {
      byLocation.set(apartment.locationId, {
        apartmentId: apartment.id,
        location: apartment.location,
        locationId: apartment.locationId,
        price: apartment.price,
      })
    }
  }

  return Array.from(byLocation.values()).sort((left, right) => {
    const leftPreferred = locationSlugs.get(left.locationId) === PREFERRED_LANDING_LOCATION_SLUG
    const rightPreferred = locationSlugs.get(right.locationId) === PREFERRED_LANDING_LOCATION_SLUG

    if (leftPreferred !== rightPreferred) {
      return leftPreferred ? -1 : 1
    }

    return left.location.localeCompare(right.location)
  })
}

function mapPopularChoice(
  popularChoice: CmsApartment['popularChoice'],
): ApartmentPopularChoice | null {
  if (!popularChoice?.enabled) {
    return null
  }

  return {
    enabled: true,
    viewsFrom: Number(popularChoice.viewsFrom ?? 0),
    viewsTo: Number(popularChoice.viewsTo ?? 0),
  }
}

export function groupApartmentsByRoomType<T extends RoomTypeGroupSource>(
  apartments: T[],
  options?: { excludeRoomType?: RoomTypeId },
): RoomTypeListingItem<T>[] {
  const source = options?.excludeRoomType
    ? apartments.filter((apartment) => apartment.roomType !== options.excludeRoomType)
    : apartments

  const groups = new Map<string, T[]>()

  for (const apartment of source) {
    const key = `${apartment.locationId}:${apartment.roomType}`
    const siblings = groups.get(key) ?? []
    siblings.push(apartment)
    groups.set(key, siblings)
  }

  return Array.from(groups.values())
    .map((variants): RoomTypeListingItem<T> | null => {
      const sortedVariants = [...variants].sort((left, right) =>
        left.name.localeCompare(right.name),
      )
      const primary =
        sortedVariants.find((variant) => variant.isPrimaryVariant) ?? sortedVariants[0]

      if (!primary) return null

      const prices = sortedVariants.map((variant) => variant.price)
      const sizes = sortedVariants.map((variant) => variant.size)
      const sizeMin = Math.min(...sizes)
      const sizeMax = Math.max(...sizes)
      const amenitySet = new Set(sortedVariants.flatMap((variant) => variant.amenities ?? []))

      return {
        ...primary,
        name: primary.roomTypeLabel,
        description: primary.description,
        images: primary.images,
        amenities: Array.from(amenitySet),
        peopleCapacity: Math.max(...sortedVariants.map((variant) => variant.peopleCapacity)),
        startingPrice: Math.min(...prices),
        size: sizeMin,
        sizeMax: sizeMin === sizeMax ? undefined : sizeMax,
        variants: sortedVariants,
      }
    })
    .filter((item): item is RoomTypeListingItem<T> => item !== null)
    .sort((left, right) => {
      const locationCompare = left.location.localeCompare(right.location)
      if (locationCompare !== 0) return locationCompare
      return left.roomTypeLabel.localeCompare(right.roomTypeLabel)
    })
}

export function getApartmentSiblings(
  apartments: ApartmentAvailabilityItem[],
  apartment: ApartmentAvailabilityItem,
): ApartmentAvailabilityItem[] {
  return apartments
    .filter(
      (candidate) =>
        candidate.locationId === apartment.locationId && candidate.roomType === apartment.roomType,
    )
    .sort((left, right) => left.name.localeCompare(right.name))
}

export function getLocationCarouselImages(
  apartments: ApartmentAvailabilityItem[],
  locationId: number,
  limit = LOCATION_CAROUSEL_IMAGE_LIMIT,
): ApartmentImage[] {
  const images: ApartmentImage[] = []
  const seenSources = new Set<string>()

  for (const apartment of apartments) {
    if (apartment.locationId !== locationId) continue

    for (const image of apartment.images) {
      if (seenSources.has(image.src)) continue

      seenSources.add(image.src)
      images.push(image)

      if (images.length >= limit) {
        return images
      }
    }
  }

  return images
}

export async function findApartments(
  payload: Payload,
  { locale, locationId, limit = APARTMENTS_LIST_LIMIT }: FindApartmentsOptions,
): Promise<ApartmentAvailabilityItem[]> {
  const result = await payload.find({
    collection: 'apartments',
    locale,
    depth: 2,
    limit,
    sort: 'title',
    overrideAccess: false,
    where: buildApartmentsWhere(locationId),
    select: APARTMENT_LIST_SELECT,
  })

  return result.docs
    .map(mapApartmentToAvailabilityItem)
    .filter((item): item is ApartmentAvailabilityItem => item !== null)
}

export async function findRoomTypeListings(
  payload: Payload,
  { locale, limit = APARTMENTS_LIST_LIMIT }: Pick<FindApartmentsOptions, 'locale' | 'limit'>,
): Promise<RoomTypeListingItem[]> {
  const apartments = await findApartments(payload, { locale, limit })
  return groupApartmentsByRoomType(apartments)
}

export async function findApartmentByCityLocationAndSlug(
  payload: Payload,
  { apartmentSlug, city, locale, locationSlug }: FindApartmentByPathOptions,
): Promise<CmsApartment | null> {
  const locationSlugCandidates = getPayloadLocationSlugCandidates(locationSlug)

  const result = await payload.find({
    collection: 'apartments',
    locale,
    depth: 2,
    limit: 1,
    overrideAccess: false,
    pagination: false,
    where: {
      and: [
        { slug: { equals: apartmentSlug } },
        { 'location.slug': { in: locationSlugCandidates } },
        { 'location.city.slug': { equals: city } },
      ],
    },
    select: APARTMENT_DETAIL_SELECT,
  })

  return result.docs?.[0] ?? null
}

export async function findApartmentBookingUrlBySlug(
  payload: Payload,
  { locale, slug }: FindApartmentBookingUrlBySlugOptions,
): Promise<string | null> {
  const trimmedSlug = slug.trim()
  if (!trimmedSlug) return null

  const result = await payload.find({
    collection: 'apartments',
    locale,
    depth: 0,
    limit: 1,
    overrideAccess: false,
    pagination: false,
    where: {
      slug: { equals: trimmedSlug },
    },
    select: {
      bookingUrl: true,
    },
  })

  return normalizeHttpsUrl(result.docs?.[0]?.bookingUrl)
}

export async function findApartmentSiblingsForDoc(
  payload: Payload,
  apartment: CmsApartment,
  locale: Locale,
): Promise<ApartmentAvailabilityItem[]> {
  const locationId =
    typeof apartment.location === 'object' && apartment.location !== null
      ? apartment.location.id
      : apartment.location

  if (locationId == null || !apartment.roomType) {
    return []
  }

  const result = await payload.find({
    collection: 'apartments',
    locale,
    depth: 2,
    limit: APARTMENTS_LIST_LIMIT,
    overrideAccess: false,
    where: {
      and: [{ location: { equals: locationId } }, { roomType: { equals: apartment.roomType } }],
    },
    select: APARTMENT_LIST_SELECT,
  })

  return result.docs
    .map(mapApartmentToAvailabilityItem)
    .filter((item): item is ApartmentAvailabilityItem => item !== null)
    .sort((left, right) => left.name.localeCompare(right.name))
}
